import { useQuery } from 'react-query';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getMonitorLimits } from 'src/services/wallet';

export const MONITOR_LIMITS_QUERY_KEY = ['wallet-monitor-limits'] as const;

export type MonitorLimits = {
  maxWallets: number | null;
  maxFunds: number | null;
  followFunds: boolean;
  hasLimits: boolean;
  usageWallets: number;
};

export type LimitValidation = {
  isValid: boolean;
  message?: string;
  availableWallets?: number | null;
  availableFunds?: number | null;
  currentWallets?: number;
  currentFunds?: number;
  maxWallets?: number | null;
  maxFunds?: number | null;
};

export function useMonitorLimits() {
  const { auth } = useAuthContext();
  const isLoggedIn = Boolean(auth?.isLoggedIn);
  const {
    data: monitorLimits,
    isLoading: isLoadingMonitorLimits,
    refetch: refetchMonitorLimits,
  } = useQuery({
    queryKey: MONITOR_LIMITS_QUERY_KEY,
    queryFn: () => getMonitorLimits(),
    enabled: isLoggedIn,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: 30000,
    staleTime: 0,
  });

  const limits: MonitorLimits = monitorLimits
    ? {
        maxWallets: monitorLimits.limits.walletQuantity,
        maxFunds: monitorLimits.limits.fundsQuantity,
        followFunds: monitorLimits.limits.followFunds,
        hasLimits: true,
        usageWallets: monitorLimits.usage.wallets,
      }
    : {
        maxWallets: null,
        maxFunds: null,
        followFunds: false,
        hasLimits: false,
        usageWallets: 0,
      };

  const validateWalletCreation = (
    currentWalletCount: number,
    isInvitedUser?: boolean,
    slotsToAdd = 1,
  ): LimitValidation => {
    if (isInvitedUser === true) {
      return { isValid: true };
    }

    if (!limits.hasLimits || limits.maxWallets === null) {
      return { isValid: true };
    }

    if (slotsToAdd <= 0) {
      return {
        isValid: true,
        availableWallets: limits.maxWallets - currentWalletCount,
        currentWallets: currentWalletCount,
        maxWallets: limits.maxWallets,
      };
    }

    const newTotal = currentWalletCount + slotsToAdd;
    const available = limits.maxWallets - currentWalletCount;

    if (newTotal > limits.maxWallets) {
      return {
        isValid: false,
        message: `Limite de carteiras atingido: ${currentWalletCount} de ${limits.maxWallets} carteiras já estão em uso. Não é possível criar outra carteira.`,
        availableWallets: available,
        currentWallets: currentWalletCount,
        maxWallets: limits.maxWallets,
      };
    }

    return {
      isValid: true,
      availableWallets: available,
      currentWallets: currentWalletCount,
      maxWallets: limits.maxWallets,
    };
  };

  const validateFundsAddition = (
    currentFundCount: number,
    fundsToAdd: number,
    isInvitedUser?: boolean,
  ): LimitValidation => {
    if (isInvitedUser === true) {
      return { isValid: true };
    }

    if (!limits.hasLimits || limits.maxFunds === null) {
      return { isValid: true };
    }

    const newTotal = currentFundCount + fundsToAdd;
    const available = limits.maxFunds - currentFundCount;

    if (newTotal > limits.maxFunds) {
      const exceeded = newTotal - limits.maxFunds;
      return {
        isValid: false,
        message: `Limite de fundos excedido: ${currentFundCount} de ${limits.maxFunds} fundos já estão em uso nesta carteira. Adicionar ${fundsToAdd} fundo(s) excederia a capacidade máxima em ${exceeded}.`,
        availableFunds: available,
        currentFunds: currentFundCount,
        maxFunds: limits.maxFunds,
      };
    }

    return {
      isValid: true,
      availableFunds: available,
      currentFunds: currentFundCount,
      maxFunds: limits.maxFunds,
    };
  };

  const getUniqueFundsCount = (funds: any[]): number => {
    const uniqueCNPJs = new Set(
      funds.map((fund) => {
        if (fund.cnpj_fundo) return fund.cnpj_fundo;
        if (fund.operation?.cnpj) return fund.operation.cnpj;
        if (fund.cnpj) return fund.cnpj;
        return fund.hashId || fund.id;
      }),
    );
    return uniqueCNPJs.size;
  };

  return {
    limits,
    monitorLimits: monitorLimits ?? null,
    isLoadingMonitorLimits,
    refetchMonitorLimits,
    validateWalletCreation,
    validateFundsAddition,
    getUniqueFundsCount,
  };
}
